import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useTheme } from "../App";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import { Building2, LayoutDashboard, Home, Package, FileText, FileSignature, Wrench, Truck, Users as UsersIcon, Key, Bell, Activity, Settings, LogOut, Moon, Sun, Menu, X, ChevronRight, Database, CreditCard, Receipt } from "lucide-react";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  var navItems = [
    { path: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { path: "/properties", label: t("nav.properties"), icon: Home },
    { path: "/assets", label: t("nav.assets"), icon: Package },
    { path: "/rentals", label: t("nav.rentals"), icon: Building2 },
    { path: "/keys", label: t("nav.keys"), icon: Key },
    { path: "/contracts", label: t("nav.contracts"), icon: FileSignature },
    { path: "/documents", label: t("nav.documents"), icon: FileText },
    { path: "/services", label: t("nav.services"), icon: Wrench },
    { path: "/reminders", label: t("nav.reminders"), icon: Bell },
    { path: "/suppliers", label: t("nav.suppliers"), icon: Truck },
    { path: "/users", label: t("nav.users"), icon: UsersIcon, roles: ["admin", "realestate_admin", "realestate_manager"] },
    { path: "/subscriptions", label: t("nav.subscriptions"), icon: CreditCard, roles: ["admin"] },
    { path: "/billing", label: t("nav.billing"), icon: Receipt, roles: ["realestate_admin", "realestate_manager"] },
    { path: "/activity", label: t("nav.activityLog"), icon: Activity, roles: ["admin"] },
    { path: "/data-management", label: t("nav.dataManagement"), icon: Database, roles: ["admin"] },
  ];

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  var filteredNavItems = [];
  for (var i = 0; i < navItems.length; i++) {
    var item = navItems[i];
    if (!item.roles || item.roles.indexOf(user?.role) >= 0) {
      filteredNavItems.push(item);
    }
  }

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={function() { setSidebarOpen(false); }} />}

      <aside className={"fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-card border-r transform transition-transform duration-200 ease-in-out " + (sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")} data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="h-36 flex items-center justify-between px-4 border-b">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_bd8042f0-b1af-4ffb-b95c-6b13c2cafb43/artifacts/citl3dkd_logo_basic.png" alt="Real Estate Manager" className="h-32 w-auto" />
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={function() { setSidebarOpen(false); }}><X className="w-5 h-5" /></Button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavItems.map(function(navItem) {
                var isActive = location.pathname === navItem.path;
                return (
                  <Link key={navItem.path} to={navItem.path} className={"sidebar-link " + (isActive ? "active" : "")} onClick={function() { setSidebarOpen(false); }} data-testid={"nav-" + navItem.path.slice(1)}>
                    <navItem.icon className="w-5 h-5" />
                    <span>{navItem.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          <div className="p-4 border-t">
            <Link to="/settings" className={"sidebar-link " + (location.pathname === "/settings" ? "active" : "")} onClick={function() { setSidebarOpen(false); }} data-testid="nav-settings">
              <Settings className="w-5 h-5" />
              <span>{t("nav.settings")}</span>
            </Link>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-lg border-b">
          <div className="h-full px-4 lg:px-6 flex items-center justify-between">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={function() { setSidebarOpen(true); }} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
            <div className="hidden lg:block"><h1 className="text-lg font-semibold capitalize">{location.pathname.slice(1) || t("nav.dashboard")}</h1></div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-header">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 pl-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-primary text-primary-foreground text-sm">{user?.name?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback></Avatar>
                    <span className="hidden md:inline font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col"><span>{user?.name}</span><span className="text-xs text-muted-foreground font-normal">{user?.email}</span></div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/settings" className="cursor-pointer"><Settings className="w-4 h-4 mr-2" />{t("nav.settings")}</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />{t("auth.logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6"><div className="animate-fade-in">{children}</div></main>
      </div>
    </div>
  );
}
